import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAccess, addAccess, updateAccess } from "../../utils/access";
import { notify } from "../../utils/utils";
import SupportLoader from "../commonComponents/SupportLoader/index.tsx";

const AVAILABLE_SERVICES = [
  { label: "Prelogix", value: "prelogix" },
  { label: "Catalist", value: "catalist" },
];

export default function Access() {
  const params = useParams();
  const navigate = useNavigate();

  const [access, setAccess] = useState<any>(null);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);

  const accessId = useMemo(() => access?.id || access?._id || null, [access]);

  useEffect(() => {
    let isMounted = true;

    if (!params.workspaceId) {
      setAccess(null);
      setSelectedServices([]);
      setIsLoadingAccess(false);
      return;
    }

    setIsLoadingAccess(true);

    getAccess(params.workspaceId)
      .then((res) => {
        if (!isMounted) {
          return;
        }

        const payload = res?.data ?? res;
        const record = Array.isArray(payload)
          ? payload[0] ?? null
          : payload ?? null;

        setAccess(record);
        setSelectedServices(record?.allowedServices ?? []);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        const message =
          (error && error.message) || error || "Failed to fetch access";
        notify(message, "error");
        setAccess(null);
        setSelectedServices([]);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingAccess(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [params.workspaceId]);

  const handleOnAddAccess = async () => {
    if (!params.workspaceId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await addAccess(params.workspaceId, selectedServices);
      const payload = response?.data ?? response;
      const allowed = payload?.allowedServices ?? selectedServices;
      setAccess(payload ?? { allowedServices: allowed });
      setSelectedServices(allowed);
      notify("Access saved successfully");
    } catch (error) {
      const message =
        (error && error.message) || error || "Failed to save access";
      notify(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnUpdateAccess = async () => {
    if (!params.workspaceId || !accessId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await updateAccess(
        accessId,
        params.workspaceId,
        selectedServices
      );
      const payload = response?.data ?? response;
      const allowed = payload?.allowedServices ?? selectedServices;
      setAccess(payload ?? { ...access, allowedServices: allowed });
      setSelectedServices(allowed);
      notify("Access updated successfully");
    } catch (error) {
      const message =
        (error && error.message) || error || "Failed to update access";
      notify(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (accessId) {
      await handleOnUpdateAccess();
    } else {
      await handleOnAddAccess();
    }
  };

  const toggleService = (service: string) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((item) => item !== service)
        : [...prev, service]
    );
  };

  if (isLoadingAccess) {
    return (
      <div className="mt-[45px] flex w-full items-center justify-center">
        <SupportLoader />
      </div>
    );
  }

  return (
    <div className="mt-[45px] flex w-full flex-col">
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate("/workspace")}
          className="text-left text-[#6157ff] hover:underline"
        >
          ← Back
        </button>
      </div>
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-[420px] rounded-lg border border-[#edf2f7] bg-white p-6 shadow-sm"
      >
        <div className="text-[20px] font-semibold text-[#1f2933]">
          Extra Access
        </div>

        <div className="mt-4 space-y-3">
          {AVAILABLE_SERVICES.map((service) => (
            <label
              key={service.value}
              className="flex cursor-pointer items-center space-x-3 text-[16px] text-[#3e4c59]"
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-[#d2d6dc] text-[#6366f1]"
                checked={selectedServices.includes(service.value)}
                onChange={() => toggleService(service.value)}
              />
              <span>{service.label}</span>
            </label>
          ))}
        </div>

        <button
          type="submit"
          className="mt-6 w-full rounded-md bg-[#6157ff] py-2 text-center text-[16px] font-semibold text-white disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? accessId
              ? "Updating..."
              : "Saving..."
            : accessId
            ? "Update"
            : "Save"}
        </button>
      </form>
    </div>
  );
}
